import React from 'react';
import { Skeleton } from './Skeleton';

// Shared header skeleton matching PageHeader layout
const PageHeaderSkeleton = ({
  showAction = false,
  showBreadcrumb = false,
}: {
  showAction?: boolean;
  showBreadcrumb?: boolean;
}) => (
  <div className="bg-white/80 backdrop-blur-xl px-8 py-6">
    {showBreadcrumb && (
      <div className="flex items-center gap-2 mb-4">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-3" />
        <Skeleton className="h-4 w-28" />
      </div>
    )}
    <div className="flex justify-between items-start">
      <div>
        <Skeleton className="h-9 w-56 mb-2" />
        <Skeleton className="h-4 w-72" />
      </div>
      {showAction && <Skeleton className="h-10 w-36 rounded-lg" />}
    </div>
  </div>
);

// Table rows skeleton — used by list/table pages
const TableRowsSkeleton = ({ rows = 8, cols = 5 }: { rows?: number; cols?: number }) => (
  <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
    {/* Table header */}
    <div className="border-b border-gray-100 px-4 py-3 flex gap-4">
      {Array.from({ length: cols }).map((_, i) => (
        <Skeleton key={i} className={`h-4 flex-1 ${i === 0 ? 'max-w-[40%]' : ''}`} />
      ))}
    </div>
    {/* Table rows */}
    {Array.from({ length: rows }).map((_, i) => (
      <div
        key={i}
        className="px-4 py-4 flex gap-4 items-center border-b border-gray-50 last:border-0"
      >
        {Array.from({ length: cols }).map((_, j) => (
          <Skeleton
            key={j}
            className={`h-4 flex-1 ${j === 0 ? 'max-w-[40%]' : ''} ${j === cols - 1 ? 'max-w-[10%]' : ''}`}
          />
        ))}
      </div>
    ))}
  </div>
);

// Filter bar skeleton — status pills + search
const FilterBarSkeleton = () => (
  <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4 mb-6">
    <Skeleton className="h-10 w-full mb-4 rounded-lg" />
    <div className="flex gap-2 mb-4 flex-wrap">
      {Array.from({ length: 7 }).map((_, i) => (
        <Skeleton key={i} className="h-9 w-24 rounded-full" />
      ))}
    </div>
    <div className="flex gap-2 flex-wrap">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-9 w-28 rounded-lg" />
      ))}
    </div>
  </div>
);

/**
 * Skeleton for table/list pages:
 * Orders, Restaurants, Users, StaffManagement, CustomerManagement, ZoneManagement, Advertisements
 */
export const TablePageSkeleton = ({ showAction = true }: { showAction?: boolean }) => (
  <div className="min-h-screen">
    <PageHeaderSkeleton showAction={showAction} />
    <div className="py-8 px-8">
      <FilterBarSkeleton />
      <TableRowsSkeleton />
    </div>
  </div>
);

/**
 * Skeleton for Dashboard page
 */
export const DashboardSkeleton = () => (
  <div className="min-h-screen">
    {/* Header */}
    <div className="bg-white/80 backdrop-blur-xl px-8 py-6 flex items-center justify-between">
      <div>
        <Skeleton className="h-9 w-40 mb-2" />
        <Skeleton className="h-4 w-52" />
      </div>
      <Skeleton className="h-10 w-24 rounded-lg" />
    </div>

    {/* Quick Actions */}
    <div className="px-8 pt-6 pb-2">
      <Skeleton className="h-6 w-36 mb-4" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-20 rounded-xl" />
        ))}
      </div>
    </div>

    {/* Main content */}
    <div className="p-8 space-y-6">
      <Skeleton className="h-6 w-44 mb-2" />

      {/* KPI Tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-28 rounded-2xl" />
        ))}
      </div>

      {/* Operational Health + Top Restaurant */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Skeleton className="h-48 rounded-2xl" />
        <Skeleton className="h-48 rounded-2xl" />
      </div>

      {/* Top Restaurants List */}
      <Skeleton className="h-56 rounded-2xl" />

      {/* Live Feed + Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Skeleton className="h-64 rounded-2xl" />
        <Skeleton className="h-64 rounded-2xl" />
      </div>
    </div>
  </div>
);

/**
 * Skeleton for Analytics page (tabs + charts)
 */
export const AnalyticsSkeleton = () => (
  <div className="min-h-screen">
    <PageHeaderSkeleton />
    <main className="py-6 px-8">
      <div className="bg-white/80 backdrop-blur-xl rounded-3xl px-6 py-6">
        {/* Filter bar */}
        <div className="flex flex-wrap gap-3 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-36 rounded-lg" />
          ))}
        </div>

        {/* Tab nav */}
        <div className="border-b border-gray-200 mb-6">
          <div className="flex gap-8 -mb-px">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-5 w-24 mb-3" />
            ))}
          </div>
        </div>

        {/* KPI row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-2xl" />
          ))}
        </div>

        {/* Chart area */}
        <Skeleton className="h-72 rounded-2xl mb-6" />

        {/* Secondary stats */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-48 rounded-2xl" />
          ))}
        </div>
      </div>
    </main>
  </div>
);

/**
 * Skeleton for Live Orders (PendingOrdersPage) — status tabs + order card grid
 */
export const LiveOrdersSkeleton = () => (
  <div className="min-h-screen">
    <PageHeaderSkeleton showAction />

    <div className="py-8 px-8">
      {/* Status filter tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-28 rounded-full flex-shrink-0" />
        ))}
      </div>

      {/* Order cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            <div className="flex justify-between">
              <Skeleton className="h-5 w-24" />
              <Skeleton className="h-6 w-20 rounded-full" />
            </div>
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 w-32" />
            <div className="border-t border-gray-100 pt-3 flex justify-between">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-8 w-28 rounded-lg" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/**
 * Skeleton for detail pages (RestaurantViewPage, MenuPage) — breadcrumb header + sections
 */
export const DetailPageSkeleton = () => (
  <div className="min-h-screen">
    <PageHeaderSkeleton showBreadcrumb showAction />
    <div className="py-8 px-8 space-y-6">
      {/* Info section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <Skeleton className="h-6 w-40" />
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex gap-3 items-center">
              <Skeleton className="h-4 w-28 flex-shrink-0" />
              <Skeleton className="h-4 flex-1" />
            </div>
          ))}
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <Skeleton className="h-6 w-32" />
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-4 w-full" />
          ))}
        </div>
      </div>

      {/* List/table section */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
          <Skeleton className="h-6 w-36" />
          <Skeleton className="h-9 w-28 rounded-lg" />
        </div>
        <TableRowsSkeleton rows={6} cols={4} />
      </div>
    </div>
  </div>
);
